import { Box, Typography, Card } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Question } from "@app/models/question.model";
import { QuestionType, SectionType } from "@app/constants/common.const";
import QuestionDropdown from "../question-dropdown/question-dropdown.component";
import QuestionRadio from "../question-radio/question-radio.component";
import QuestionCheckbox from "../question-checkbox/question-checkbox.component";
import QuestionTextInput from "../question-text-input/question-text-input.component";
import QuestionTableRadio from "../question-table-radio/question-table-radio.component";
import QuestionCollapseExpandRadio from "../question-collapse-expand-radio/question-collapse-expand-radio.component";
import QuestionTextarea from "../question-textarea/question-textarea.component";
import { useState, useRef, useEffect } from "react";
import { useContextMenu } from "@app/hooks/useContextMenu";
import { getQuestionContextMenuItems } from "@app/utils/contextMenu.utils";
import {
  addHighlight,
  applyHighlights,
  getHighlightMenuItems,
  removeHighlight,
} from "@app/utils/highlight.utils";
import { Section } from "@app/models/section.model";
import { AnswerRequest } from "@app/types/answer.type";
import { QuestionGroup } from "@app/models/question-group.model";

const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  padding: theme.spacing(3),
  height: "100%",
  overflow: "auto",
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: "#fff",
  borderRadius: "8px",
  "& .MuiTypography-h5": {
    fontSize: "1.1rem",
    fontWeight: 500,
  },
}));

const ContentCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  flex: 1,
  overflow: "auto",
}));

const MainSection = styled(Box)(() => ({
  flex: 1,
  overflowY: "auto",
  padding: "0 8px",
}));

const GroupBox = styled(Box)(() => ({
  marginBottom: "24px",
  padding: "16px",
  backgroundColor: "#fff",
  borderRadius: "12px",
  "&:last-child": {
    marginBottom: 0,
  },
}));

const GroupContent = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(3),
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));

const ImageSection = styled(Box)(({ theme }) => ({
  flex: "0 0 50%",
  "& img": {
    width: "100%",
    height: "auto",
    borderRadius: "8px",
  },
  [theme.breakpoints.down("md")]: {
    marginBottom: theme.spacing(3),
  },
}));

const QuestionsSection = styled(Box)(({ theme }) => ({
  flex: "0 0 50%",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const GroupTitle = styled(Typography)(({ theme }) => ({
  fontSize: "18px",
  fontWeight: 600,
  marginBottom: "16px",
  color: theme.palette.primary.main,
  borderBottom: `1px solid ${theme.palette.divider}`,
  paddingBottom: "8px",
  "& .MuiTypography-h5": {
    color: "black",
    fontSize: "1.1rem",
    fontWeight: 500,
  },
}));

const QuestionBlock = styled(Box)(() => ({
  paddingRight: "12px",
  backgroundColor: "#fff",
  borderRadius: "8px",
  "&:last-child": {
    marginBottom: 0,
  },
}));

const QuestionContent = styled(Box)(({ theme }) => ({
  flex: "0 0 50%",
  paddingRight: "24px",
  [theme.breakpoints.down("md")]: {
    borderRight: "none",
    borderBottom: "1px solid #e0e0e0",
    paddingRight: 0,
    paddingBottom: "16px",
  },
}));

const QuestionHeader = styled(Typography)(({ theme }) => ({
  fontSize: "16px",
  fontWeight: 600,
  marginBottom: "12px",
  color: theme.palette.primary.main,
}));

const QuestionText = styled(Typography)(({ theme }) => ({
  fontSize: "16px",
  marginBottom: "8px",
  userSelect: "text",
  "& span[style*='background-color: yellow']": {
    backgroundColor: "yellow",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
  },
}));

interface ExamListeningProps {
  testId: number;
  currentQuestion: number;
  questionsBySection: Section[];
  onChangeAnswer: (
    value: string | string[] | Record<number, string>,
    questionId: number
  ) => void;
  onQuestionChange: (questionNumber: number) => void;
  onSelectedAnswersChange: (newAnswers: Record<number, AnswerRequest>) => void;
}

function ExamListening({
  currentQuestion,
  questionsBySection,
  onChangeAnswer,
  onQuestionChange,
  onSelectedAnswersChange,
}: ExamListeningProps) {
  const imageRef = useRef<HTMLDivElement>(null);
  const { showContextMenu } = useContextMenu();
  const [selectedAnswers, setSelectedAnswers] = useState<
    Record<number, AnswerRequest>
  >({});
  const questionRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const answerRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const audioRef = useRef<HTMLAudioElement>(null);
  const [hasPlayedAudio, setHasPlayedAudio] = useState<boolean>(false);

  if (!questionsBySection || questionsBySection.length === 0) {
    return (
      <Container>
        <HeaderCard elevation={1}>
          <Typography variant="h5">Loading...</Typography>
        </HeaderCard>
      </Container>
    );
  }

  const getCurrentSectionAndGroup = () => {
    let questionCount = 0;

    for (let i = 0; i < questionsBySection.length; i++) {
      const section = questionsBySection[i];

      if (!section.questionGroups) {
        continue;
      }

      for (const group of section.questionGroups) {
        if (!group.questions) {
          continue;
        }

        const groupQuestionCount = group.questions.length;

        if (questionCount + groupQuestionCount >= currentQuestion) {
          return {
            section,
            sectionIndex: i,
            group,
            groupStartIndex: questionCount,
            groupIndex: currentQuestion - questionCount - 1,
          };
        }

        questionCount += groupQuestionCount;
      }
    }

    return null;
  };

  const currentSectionInfo = getCurrentSectionAndGroup();

  if (!currentSectionInfo) {
    return (
      <Container>
        <HeaderCard elevation={1}>
          <Typography variant="h5">No questions available</Typography>
        </HeaderCard>
      </Container>
    );
  }

  const { group, groupStartIndex, groupIndex } = currentSectionInfo;
  const currentQuestionData = group.questions[groupIndex];

  useEffect(() => {
    scrollToQuestion(currentQuestion);
  }, [currentQuestion]);

  useEffect(() => {
    const questionElements = document.querySelectorAll(".question-text");

    questionElements.forEach((element) => {
      if (element instanceof HTMLElement) {
        const questionId = element
          .closest(".question-block")
          ?.getAttribute("data-question-id");

        if (questionId) {
          applyHighlights(
            element,
            currentSectionInfo.section.id,
            parseInt(questionId)
          );
        }
      }
    });
  }, [currentQuestion]);

  useEffect(() => {
    if (
      currentSectionInfo?.section?.data?.audio &&
      audioRef.current &&
      !hasPlayedAudio
    ) {
      audioRef.current.src = currentSectionInfo.section.data.audio;

      const handleAudioEnd = () => {
        setHasPlayedAudio(true);
      };

      audioRef.current.addEventListener("ended", handleAudioEnd);

      audioRef.current.play().catch((error) => {
        // eslint-disable-next-line no-console
        console.error("Error playing audio:", error);
      });

      return () => {
        if (audioRef.current) {
          audioRef.current.removeEventListener("ended", handleAudioEnd);
        }
      };
    }
  }, []);

  useEffect(() => {
    setHasPlayedAudio(false);
  }, [currentQuestion]);

  const scrollToQuestion = (questionNumber: number) => {
    const questionRef = questionRefs.current[questionNumber];
    const answerRef = answerRefs.current[questionNumber];

    if (questionRef) {
      questionRef.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    if (answerRef) {
      answerRef.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleAnswerChange = (
    questionNumber: number,
    value: string | string[] | Record<number, string> | any,
    question: any,
    questionType?: QuestionType
  ) => {
    if (questionType === QuestionType.TABLE_RADIO) {
      const { question, selectedOption } = value;

      onChangeAnswer(selectedOption, question.questionNumber);
      setSelectedAnswers((prev) => {
        const updatedAnswers = {
          ...prev,
          [question.questionNumber]: {
            questionId: question.id,
            data: {
              textValue: selectedOption,
            },
          },
        };

        onSelectedAnswersChange(updatedAnswers);

        return updatedAnswers;
      });
    } else if (questionType === QuestionType.CHECKBOX) {
      const { questions, selectedOption } = value;

      questions.forEach((question: any, index: number) => {
        setTimeout(() => {
          onChangeAnswer(selectedOption, question.questionNumber);
        }, index * 200);
        setSelectedAnswers((prev) => {
          const updatedAnswers = {
            ...prev,
            [question.questionNumber]: {
              questionId: question.id,
              data: {
                textValue: selectedOption,
              },
            },
          };

          onSelectedAnswersChange(updatedAnswers);

          return updatedAnswers;
        });
      });
    } else {
      onChangeAnswer(value, questionNumber);

      setSelectedAnswers((prev) => {
        const updatedAnswers = {
          ...prev,
          [questionNumber]: {
            questionId: question.id,
            data: {
              textValue: value,
            },
          },
        };

        onSelectedAnswersChange(updatedAnswers);

        return updatedAnswers;
      });
    }
  };

  const handleFocusAnswer = (questionNumber: number) => {
    onQuestionChange(questionNumber);
  };

  const renderQuestionInput = (
    question: Question,
    index: number,
    value: string | string[] | Record<number, string> | any,
    onChange: (value: string | string[] | Record<number, string> | any) => void,
    onFocus: (questionNumber: number) => void,
    currentGroup: QuestionGroup,
    firstQuestionNumber?: number
  ) => {
    const questionNumber = groupStartIndex + index + 1;

    const tableRadioQuestions = currentGroup.questions.filter(
      (q) => q.group.type === QuestionType.TABLE_RADIO
    );

    const checkboxQuestions = currentGroup.questions.filter(
      (q) => q.group.type === QuestionType.CHECKBOX
    );

    switch (question.group.type) {
      case QuestionType.DROPDOWN:
        return (
          <QuestionDropdown
            questionNumber={questionNumber}
            options={question.data.answer || []}
            value={value as string}
            onChange={onChange as (value: string) => void}
            onFocus={onFocus as (questionNumber: number) => void}
          />
        );
      case QuestionType.RADIO:
        return (
          <QuestionRadio
            questionNumber={questionNumber}
            options={question.data.answer || []}
            value={value as string}
            onChange={onChange as (value: string) => void}
            onFocus={onFocus as (questionNumber: number) => void}
          />
        );
      case QuestionType.TABLE_RADIO:
        if (tableRadioQuestions[0]?.id === question.id) {
          const options = tableRadioQuestions[0].data.answer || [];

          return (
            <QuestionTableRadio
              questions={
                tableRadioQuestions.map((question, index) => {
                  return {
                    ...question,
                    questionNumber: getQuestionNumber(
                      currentSectionInfo.sectionIndex,
                      currentSectionInfo.groupIndex,
                      index
                    ),
                  };
                }) as Question[]
              }
              options={options}
              value={value as Record<string, string>}
              onChange={(_rowIndex: string, selectedOption: string) => {
                onChange({
                  question,
                  selectedOption,
                });
              }}
              onFocus={(questionNumber: number) => {
                onFocus(questionNumber);
              }}
            />
          );
        }

        return null;
      case QuestionType.COLLAPSE_EXPAND_RADIO:
        return (
          <QuestionCollapseExpandRadio
            questionNumber={questionNumber}
            question={question}
            options={question.data.answer || []}
            value={value as string}
            onChange={(questionIndex: number, selectedOption: string) => {
              onChange(selectedOption);
            }}
            onFocus={onFocus as (questionNumber: number) => void}
          />
        );
      case QuestionType.CHECKBOX:
        if (checkboxQuestions[0].id === question.id) {
          const options = checkboxQuestions[0].data.answer || [];

          return (
            <QuestionCheckbox
              options={options}
              questions={
                checkboxQuestions.map((question, index) => {
                  return {
                    ...question,
                    questionNumber: firstQuestionNumber! + index,
                  };
                }) as Question[]
              }
              value={value as string[]}
              onChange={(questions: Question[], value: string[]) => {
                onChange({
                  questions,
                  selectedOption: value,
                });
              }}
              onFocus={onFocus as (questionNumber: number) => void}
            />
          );
        }

        return null;
      case QuestionType.IMAGE_TEXT_INPUT:
        return (
          <QuestionTextInput
            questionNumber={questionNumber}
            value={value as string}
            onChange={onChange as (value: string) => void}
            onFocus={onFocus as (questionNumber: number) => void}
          />
        );
      case QuestionType.TEXTAREA:
        return (
          <QuestionTextarea
            questionNumber={questionNumber}
            value={value as string}
            onChange={onChange as (value: string) => void}
          />
        );
      default:
        return (
          <QuestionRadio
            questionNumber={questionNumber}
            options={question.data.answer || []}
            value={value as string}
            onChange={onChange as (value: string) => void}
            onFocus={onFocus as (questionNumber: number) => void}
          />
        );
    }
  };

  const handleClearAnswer = (questionNumber: number) => {
    handleAnswerChange(questionNumber, "", null);
  };

  const handleHighlight = (
    sectionNumber: number,
    questionId: number,
    range: Range
  ) => {
    const textNode = range.startContainer;

    if (textNode.nodeType === Node.TEXT_NODE) {
      addHighlight(
        sectionNumber,
        questionId,
        textNode.textContent || "",
        range.startOffset,
        range.endOffset
      );
      applyHighlights(
        textNode.parentElement as HTMLElement,
        sectionNumber,
        questionId
      );
    }
  };

  const handleRemoveHighlight = (
    sectionNumber: number,
    questionId: number,
    element: HTMLElement
  ) => {
    const highlightIndex = element.getAttribute("data-highlight");

    if (highlightIndex !== null) {
      removeHighlight(sectionNumber, questionId, parseInt(highlightIndex));
      applyHighlights(
        element.parentElement as HTMLElement,
        sectionNumber,
        questionId
      );
    }
  };

  const handleContextMenu = (
    event: React.MouseEvent,
    questionNumber: number,
    sectionNumber: number
  ) => {
    const selection = window.getSelection();
    const questionId = currentQuestionData.id;

    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const targetElement = event.target as HTMLElement;
      const isHighlighted = targetElement.closest("[data-highlight]");

      if (!range.collapsed) {
        showContextMenu(
          event,
          getHighlightMenuItems(
            () => handleHighlight(sectionNumber, questionId, range),
            isHighlighted
              ? () =>
                  handleRemoveHighlight(
                    sectionNumber,
                    questionId,
                    isHighlighted as HTMLElement
                  )
              : undefined
          )
        );
      } else if (isHighlighted) {
        showContextMenu(
          event,
          getHighlightMenuItems(
            () => {},
            () =>
              handleRemoveHighlight(
                sectionNumber,
                questionId,
                isHighlighted as HTMLElement
              )
          )
        );
      } else {
        showContextMenu(
          event,
          getQuestionContextMenuItems(
            SectionType.LISTENING,
            handleClearAnswer,
            questionNumber
          )
        );
      }
    } else {
      showContextMenu(
        event,
        getQuestionContextMenuItems(
          SectionType.LISTENING,
          handleClearAnswer,
          questionNumber
        )
      );
    }
  };

  const getQuestionNumber = (
    sectionIndex: number,
    groupIndex: number,
    questionIndex: number
  ): number => {
    let questionNumber = 1;

    for (let i = 0; i < sectionIndex; i++) {
      const section = questionsBySection[i];

      if (!section.questionGroups) continue;

      for (let j = 0; j < section.questionGroups.length; j++) {
        const group = section.questionGroups[j];

        if (!group.questions) continue;

        questionNumber += group.questions.length;
      }
    }

    const currentSection = questionsBySection[sectionIndex];

    if (currentSection.questionGroups) {
      for (let j = 0; j < groupIndex; j++) {
        const group = currentSection.questionGroups[j];

        if (!group.questions) continue;

        questionNumber += group.questions.length;
      }
    }

    return questionNumber + questionIndex;
  };

  return (
    <Container>
      <HeaderCard elevation={1}>
        <Typography variant="h5">{currentSectionInfo.section.title}</Typography>
      </HeaderCard>

      {currentSectionInfo.section.data.audio && (
        <audio ref={audioRef} preload="auto" style={{ display: "none" }}>
          Your browser does not support the audio element.
        </audio>
      )}

      <ContentCard elevation={1}>
        <MainSection>
          {currentSectionInfo.section.questionGroups?.map(
            (group, groupIndex) => {
              if (!group.questions) {
                return null;
              }

              const firstQuestionNumber = getQuestionNumber(
                currentSectionInfo.sectionIndex,
                groupIndex,
                0
              );
              const lastQuestionNumber = getQuestionNumber(
                currentSectionInfo.sectionIndex,
                groupIndex,
                group.questions.length - 1
              );

              return (
                <GroupBox key={groupIndex}>
                  <GroupTitle>
                    Questions {firstQuestionNumber} - {lastQuestionNumber}
                    <Typography variant="h5">{group.title}</Typography>
                  </GroupTitle>
                  <GroupContent>
                    {(group.data?.image ||
                      currentSectionInfo.section.data?.image) && (
                      <ImageSection ref={imageRef}>
                        <img
                          src={
                            group.data?.image ||
                            currentSectionInfo.section.data?.image
                          }
                          alt={`Group ${groupIndex + 1} image`}
                        />
                      </ImageSection>
                    )}
                    <QuestionsSection>
                      {![
                        QuestionType.CHECKBOX,
                        QuestionType.TABLE_RADIO,
                      ].includes(group.type) &&
                        group.questions.map((question, index) => {
                          const questionNumber = getQuestionNumber(
                            currentSectionInfo.sectionIndex,
                            groupIndex,
                            index
                          );

                          return (
                            <QuestionBlock
                              key={question.id || index}
                              className="question-block"
                              data-question-id={question.id}
                              ref={(el: HTMLDivElement | null) => {
                                questionRefs.current[questionNumber] = el;
                              }}
                              onContextMenu={(e) =>
                                handleContextMenu(
                                  e,
                                  questionNumber,
                                  currentSectionInfo.section.id
                                )
                              }
                            >
                              <QuestionContent>
                                <QuestionHeader>
                                  Question {questionNumber}
                                </QuestionHeader>
                                <QuestionText className="question-text">
                                  {question.text}
                                </QuestionText>
                              </QuestionContent>
                              {renderQuestionInput(
                                question,
                                index,
                                selectedAnswers[questionNumber]?.data
                                  ?.textValue ||
                                  (question.data?.question_type ===
                                  QuestionType.CHECKBOX
                                    ? []
                                    : ""),
                                (value) =>
                                  handleAnswerChange(
                                    questionNumber,
                                    value,
                                    question
                                  ),
                                () => handleFocusAnswer(questionNumber),
                                group
                              )}
                            </QuestionBlock>
                          );
                        })}
                      {group.type === QuestionType.TABLE_RADIO &&
                        group.questions.map((question, index) => {
                          const questionNumber = getQuestionNumber(
                            currentSectionInfo.sectionIndex,
                            currentSectionInfo.groupIndex,
                            index
                          );
                          const lastQuestionNumber = getQuestionNumber(
                            currentSectionInfo.sectionIndex,
                            currentSectionInfo.groupIndex,
                            group.questions.length - 1
                          );

                          if (index === 0) {
                            return (
                              <QuestionBlock
                                key={question.id || index}
                                className="question-block"
                                data-question-id={question.id}
                                ref={(el: HTMLDivElement | null) => {
                                  questionRefs.current[questionNumber] = el;
                                }}
                                onContextMenu={(e) =>
                                  handleContextMenu(
                                    e,
                                    questionNumber,
                                    currentSectionInfo.section.id
                                  )
                                }
                              >
                                <Typography variant="h6">
                                  Question {questionNumber} -{" "}
                                  {lastQuestionNumber}.
                                </Typography>

                                {renderQuestionInput(
                                  question,
                                  index,
                                  selectedAnswers,
                                  (value) =>
                                    handleAnswerChange(
                                      questionNumber,
                                      value,
                                      question,
                                      question.group.type
                                    ),
                                  (questionNumber) =>
                                    handleFocusAnswer(questionNumber),
                                  group
                                )}
                              </QuestionBlock>
                            );
                          }
                        })}
                      {group.type === QuestionType.CHECKBOX &&
                        group.questions.map((question, index) => {
                          if (index === 0) {
                            return (
                              <QuestionBlock
                                key={question.id}
                                className="question-block"
                                data-question-id={question.id}
                                ref={(el: HTMLDivElement | null) => {
                                  questionRefs.current[firstQuestionNumber] =
                                    el;
                                }}
                                onContextMenu={(e) =>
                                  handleContextMenu(
                                    e,
                                    currentSectionInfo.section.id,
                                    firstQuestionNumber
                                  )
                                }
                              >
                                {/* <Typography variant="h6">
                                  Question {firstQuestionNumber} -{" "}
                                  {lastQuestionNumber}.
                                </Typography> */}
                                <QuestionContent>
                                  <QuestionText className="question-text">
                                    {question.text}
                                  </QuestionText>
                                </QuestionContent>

                                {renderQuestionInput(
                                  question,
                                  index,
                                  selectedAnswers[firstQuestionNumber]?.data
                                    ?.textValue,
                                  (value) =>
                                    handleAnswerChange(
                                      firstQuestionNumber,
                                      value,
                                      question,
                                      question.group.type
                                    ),
                                  (firstQuestionNumber) =>
                                    handleFocusAnswer(firstQuestionNumber),
                                  group,
                                  firstQuestionNumber
                                )}
                              </QuestionBlock>
                            );
                          }
                        })}
                    </QuestionsSection>
                  </GroupContent>
                </GroupBox>
              );
            }
          )}
        </MainSection>
      </ContentCard>
    </Container>
  );
}

export default ExamListening;
